import { onlyDefined } from '@acme/common'
import { zod } from '@acme/core'
import { z } from 'zod'
import ApiService from '../api.service'

const numberNullish = z.coerce.number().nonnegative().nullish()
const stringNullish = z.coerce.string().nullish()
const booleanNullish = z.coerce
    .boolean()
    .nullish()
    .transform(it => {
        return !it ? 'N' : 'S'
    })
const timeFormat = (nullable: boolean) =>
    (nullable
        ? stringNullish
        : z.coerce.string().nonempty('Formato inválido')
    ).transform(it => {
        if (!it && nullable) return null
        const regex = new RegExp('^[0-9]{2}:[0-9]{2}$')
        if (!regex.test(it || '')) throw new Error('Formato inválido')
        return it
    })

const horasExtrasSchema = z
    .object({
        todas: z.boolean().default(false),
        porcentagemDemaisHrs: numberNullish,
        porcentagemPrimeirasHrs: numberNullish,
        qtdPrimeirasHrs: stringNullish,
        prioridadeCompensacao: stringNullish,
        controleTurno: booleanNullish,
    })
    .nullish()

const regrasCalculoSchema = zod.RegrasCalculoSchema.pick({
    id: true,
    clienteId: true,
    nome: true,
}).extend({
    horasExtras: z.record(horasExtrasSchema),
    adicionalNoturno: numberNullish,
    compensarJustificadas: booleanNullish,
    compensarInjustificadas: booleanNullish,
    compensacaoDeHoras: booleanNullish,
    horasExtrasFeriado: booleanNullish,
    hrsExtrasNoturnas: booleanNullish,
    compensarHrsExtrasNoturnas: booleanNullish,
    tipoHorasExcedentes: z
        .enum(['DIARIO', 'SEMANAL', 'MENSAL'])
        .default('DIARIO'),
    toleranciaCLT: booleanNullish,
    controlarBancoHoras: booleanNullish,
    maximoHorasDiarias: timeFormat(true),
    horasInterjornada: timeFormat(false),
    compensacaoManual: booleanNullish,
    calcularHorasSuprimidas: booleanNullish,
    faltaAutomatica: z.enum(['injustificada', 'justificada']).nullish(),
    horasFaltaAutomatica: timeFormat(true),
    porcentagens: z
        .array(
            z.object({
                id: numberNullish,
                porcentagem: z.coerce.number().nonnegative(),
                compensacao: z.number(),
                persiste: z.boolean().default(true),
            }),
        )
        .transform(values => values.filter(onlyDefined))
        .refine(
            data => {
                const porcentagens = data.map(item => item.porcentagem)
                return new Set(porcentagens).size === porcentagens.length
            },
            {
                message: 'Não pode ter duas ou mais porcentagens iguais',
            },
        ),
    horasExcedentesConsideraDiaInteiro: booleanNullish,
})

export const createRegrasCalculoSchema = regrasCalculoSchema.omit({
    id: true,
    clienteId: true,
})

export const updateRegrasCalculoSchema = regrasCalculoSchema.required({
    id: true,
    clienteId: true,
})

export type RegraCalculoPresenter = Omit<
    z.infer<typeof regrasCalculoSchema>,
    'porcentagens'
> & {
    Porcentagens: PorcentagemRegraCalculo[]
    porcentagens: PorcentagemRegraCalculo[]
}

export type PorcentagemRegraCalculo = z.infer<
    typeof regrasCalculoSchema.shape.porcentagens
>[0]

export const regrasCalculo = new ApiService<RegraCalculoPresenter>(
    'regras-calculo',
)
